// Admin API client — /admin/docs/* (mirrors the API's docs aggregate).

import type {
  DocPage,
  DocPageRevision,
  DocsCreateRequest,
  DocsUpdateRequest,
  DocsPublishBatchResponse,
  DocsNavResponse,
  DocsNavUpdateRequest,
  DocsRedirectListResponse,
  DocsRedirectCreateRequest,
  DocsSuggestion,
  ContentRedirect,
} from '@a7y/types';
import { client } from './transport';
import { contentMutate, contentSectionApi } from './content-transport';

export const docsApi = {
  // ------------------------------------------------------------------------------------
  // Docs CMS (A7Y-4470). Nested under `/docs` (the admin_docs_route sub-router).
  // Built on the same `contentSectionApi` factory as blog, plus the docs-only
  // batch publish, nav config and redirects endpoints.
  docs: {
    ...contentSectionApi<DocPage, DocsCreateRequest, DocsUpdateRequest, DocPageRevision>('/docs'),

    /** `POST /admin/docs/publish-batch` (A7Y-4470 D7) — at most 100 ids. */
    publishBatch: (ids: string[]) =>
      contentMutate<DocsPublishBatchResponse['data']>('/docs/publish-batch', 'POST', { ids }),

    nav: {
      get: () => client.get<DocsNavResponse>('/docs/nav'),
      put: (body: DocsNavUpdateRequest) => contentMutate<DocsNavResponse>('/docs/nav', 'PUT', body),
    },

    redirects: {
      list: () => client.get<DocsRedirectListResponse>('/docs/redirects'),
      create: (body: DocsRedirectCreateRequest) =>
        contentMutate<ContentRedirect>('/docs/redirects', 'POST', body),
      remove: (id: string) => contentMutate<{ success: true }>(`/docs/redirects/${id}`, 'DELETE'),
    },

    /** A7Y-4480 — agent suggestions on one page; apply writes the working copy, never publishes. */
    suggestions: {
      list: (pageId: string) =>
        client.get<{ suggestions: DocsSuggestion[] }>(`/docs/${pageId}/suggestions`),
      apply: (pageId: string, suggestionId: string) =>
        contentMutate<{ suggestion: DocsSuggestion; page: DocPage }>(
          `/docs/${pageId}/suggestions/${suggestionId}/apply`,
          'POST'
        ),
      dismiss: (pageId: string, suggestionId: string) =>
        contentMutate<DocsSuggestion>(
          `/docs/${pageId}/suggestions/${suggestionId}/dismiss`,
          'POST'
        ),
    },
  },
};
